import React, { useEffect, useState } from "react";
import { Trophy, PackageCheck, PackageX } from "lucide-react";
import confetti from "canvas-confetti";
import api from "../../lib/api";
import { useCurrency } from "../../lib/contexts";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { toast } from "sonner";

export const GST_RATES = [0, 5, 12, 18, 28];

// Derives the Proforma Invoice figures. EXW amount is the booked deal value.
export function wonAmounts({ quantity_mt, exw_price, freight_amount, gst_percent }) {
  const q = Number(quantity_mt) || 0;
  const p = Number(exw_price) || 0;
  const f = Number(freight_amount) || 0;
  const g = Number(gst_percent) || 0;
  const exwAmount = q * p;
  const freightPerMt = q ? f / q : 0;
  const forPricePerMt = p + freightPerMt;
  const forAmount = exwAmount + f;
  const gstAmount = (forAmount * g) / 100;
  return { q, p, f, g, exwAmount, freightPerMt, forPricePerMt, forAmount, gstAmount, grandTotal: forAmount + gstAmount };
}

// Product rows of a deal, synthesising one from the legacy single-product fields.
export function dealProducts(deal) {
  if (!deal) return [];
  if (Array.isArray(deal.products) && deal.products.length) return deal.products;
  if (!deal.product_name) return [];
  return [{
    id: deal.id, product_name: deal.product_name, product_code: deal.product_code,
    status: deal.stage === "Won" ? "won" : "open",
    po_number: deal.po_number, pi_number: deal.pi_number, quantity_mt: deal.quantity_mt,
    exw_price: deal.exw_price, freight_amount: deal.freight_amount, gst_percent: deal.gst_percent,
    exw_amount: deal.exw_amount, for_amount: deal.for_amount, gst_amount: deal.gst_amount,
    grand_total: deal.grand_total, freight_per_mt: deal.freight_per_mt, for_price_per_mt: deal.for_price_per_mt,
  }];
}

// Two-burst confetti fired from the bottom corners for a "yay we won!" moment.
function celebrate() {
  const end = Date.now() + 1400;
  const colors = ["#059669", "#10b981", "#facc15", "#f59e0b", "#3b82f6"];
  (function frame() {
    confetti({ particleCount: 4, angle: 60, spread: 65, startVelocity: 55, origin: { x: 0, y: 0.85 }, colors, disableForReducedMotion: true });
    confetti({ particleCount: 4, angle: 120, spread: 65, startVelocity: 55, origin: { x: 1, y: 0.85 }, colors, disableForReducedMotion: true });
    if (Date.now() < end) requestAnimationFrame(frame);
  })();
}

const emerald = "border-2 border-emerald-200 focus-visible:border-emerald-500 focus-visible:ring-emerald-500/40";

export const LOST_REASON_MIN = 50;

const toRow = (p, i) => ({
  key: p.id || `row-${i}`,
  id: p.id,
  product_name: p.product_name || "",
  product_code: p.product_code || "",
  status: p.status === "not_taken" ? "not_taken" : "won",
  lostReason: p.lost_reason || "",
  po: p.po_number || "",
  pi: p.pi_number || "",
  qty: p.quantity_mt ?? "",
  exw: p.exw_price ?? "",
  freight: p.freight_amount ?? "",
  gst: String(p.gst_percent ?? 18),
});

function ProductBlock({ row, index, many, onChange, format }) {
  const taken = row.status === "won";
  const { exwAmount, freightPerMt, forPricePerMt, forAmount, gstAmount, grandTotal } = wonAmounts({
    quantity_mt: row.qty, exw_price: row.exw, freight_amount: row.freight, gst_percent: row.gst,
  });
  const set = (k) => (e) => onChange(index, { [k]: e.target.value });
  return (
    <div data-testid={`won-product-${index}`}
      className={`rounded-md border p-3 space-y-3 ${taken ? "border-emerald-200 bg-white" : "border-border bg-secondary/40"}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <span className={`font-semibold text-sm ${taken ? "" : "text-muted-foreground line-through"}`}>{row.product_name || "—"}</span>
          {row.product_code && (
            <span className="text-[10px] font-mono bg-neutral-200 px-1.5 py-0.5 rounded">{row.product_code}</span>
          )}
        </div>
        {many && (
          <div className="flex bg-neutral-100 rounded-md p-0.5">
            {[["won", "Won", PackageCheck], ["not_taken", "Not taken", PackageX]].map(([val, label, Icon]) => (
              <button key={val} type="button" data-testid={`won-status-${index}-${val}`}
                onClick={() => onChange(index, { status: val })}
                className={`flex items-center gap-1 px-2.5 py-1 text-[11px] font-semibold rounded transition-colors ${
                  row.status === val
                    ? val === "won" ? "bg-emerald-600 text-white" : "bg-neutral-500 text-white"
                    : "text-neutral-500 hover:text-neutral-800"}`}>
                <Icon className="w-3 h-3" />{label}
              </button>
            ))}
          </div>
        )}
      </div>

      {!taken ? (
        <div className="space-y-1.5">
          <Label className="text-xs font-semibold">
            Reason this product was lost <span className="text-destructive">*</span>
          </Label>
          <Textarea data-testid={`won-lost-reason-${index}`} rows={3} value={row.lostReason}
            onChange={set("lostReason")}
            placeholder="What made the customer leave this product out? Price, specification, a competitor, timing…"
            className="border-2 border-neutral-300 focus-visible:border-neutral-500" />
          <div className="flex items-center justify-between text-[11px]">
            <span className={row.lostReason.trim().length >= LOST_REASON_MIN ? "text-emerald-700 font-medium" : "text-amber-700 font-medium"}>
              {row.lostReason.trim().length} / {LOST_REASON_MIN} characters minimum
              {row.lostReason.trim().length < LOST_REASON_MIN && " — needed before this can be saved"}
            </span>
            <span className="text-muted-foreground italic">Kept on the deal and excluded from every figure.</span>
          </div>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-semibold">Our PI Number <span className="text-destructive">*</span></Label>
              <Input data-testid={`won-pi-number-${index}`} value={row.pi} onChange={set("pi")}
                placeholder="e.g. SNF/PI/26-27/0091" className={emerald} />
            </div>
            <div>
              <Label className="text-xs font-semibold">Customer PO Number</Label>
              <Input data-testid={`won-po-number-${index}`} value={row.po} onChange={set("po")}
                placeholder="Optional" className={emerald} />
            </div>
            <div>
              <Label className="text-xs font-semibold">Quantity (MT) <span className="text-destructive">*</span></Label>
              <Input data-testid={`won-quantity-${index}`} type="number" step="0.001" min="0" value={row.qty}
                onChange={set("qty")} placeholder="e.g. 12.5" className={`${emerald} font-semibold`} />
            </div>
            <div>
              <Label className="text-xs font-semibold">EXW Price (per MT) <span className="text-destructive">*</span></Label>
              <Input data-testid={`won-exw-price-${index}`} type="number" step="0.01" min="0" value={row.exw}
                onChange={set("exw")} placeholder="e.g. 42000" className={`${emerald} font-semibold`} />
            </div>
            <div>
              <Label className="text-xs font-semibold">Freight (full amount, e.g. 20,000)</Label>
              <Input data-testid={`won-freight-${index}`} type="number" step="0.01" min="0" value={row.freight}
                onChange={set("freight")} placeholder="0" className={emerald} />
            </div>
            <div>
              <Label className="text-xs font-semibold">GST (%)</Label>
              <select data-testid={`won-gst-${index}`} value={row.gst} onChange={set("gst")}
                className="w-full h-10 rounded-md border-2 border-emerald-200 bg-white px-3 text-sm focus:outline-none focus:border-emerald-500">
                {GST_RATES.map((r) => <option key={r} value={r}>{r}%</option>)}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3" data-testid={`won-summary-${index}`}>
            <div className="rounded-md border border-emerald-200 bg-white p-3 space-y-1.5 text-sm">
              <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-semibold pb-1 border-b border-emerald-100">Per MT rates</div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">EXW Price (per MT)</span>
                <span className="tabular-nums" data-testid={`won-exw-price-display-${index}`}>{format(Number(row.exw) || 0)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Freight (per MT)</span>
                <span className="tabular-nums" data-testid={`won-freight-per-mt-${index}`}>{format(freightPerMt)}</span>
              </div>
              <div className="flex items-center justify-between border-t border-emerald-100 pt-1.5">
                <span className="font-semibold text-emerald-800">Total FOR Price (per MT)</span>
                <span className="font-bold tabular-nums text-emerald-800" data-testid={`won-for-price-per-mt-${index}`}>{format(forPricePerMt)}</span>
              </div>
            </div>
            <div className="rounded-md border border-emerald-200 bg-emerald-50/60 p-3 space-y-1.5 text-sm">
              <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-semibold pb-1 border-b border-emerald-200">
                Product totals {Number(row.qty) > 0 && <span className="font-normal normal-case tracking-normal text-muted-foreground">· {row.qty} MT</span>}
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Total EXW Value</span>
                <span className="tabular-nums" data-testid={`won-exw-amount-${index}`}>{format(exwAmount)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Total Freight</span>
                <span className="tabular-nums" data-testid={`won-freight-amount-${index}`}>{format(Number(row.freight) || 0)}</span>
              </div>
              <div className="flex items-center justify-between border-t border-emerald-200 pt-1.5">
                <span className="text-muted-foreground">Total FOR Value <span className="text-[10px]">(EXW + Freight)</span></span>
                <span className="font-semibold tabular-nums" data-testid={`won-for-amount-${index}`}>{format(forAmount)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">GST @ {Number(row.gst) || 0}%</span>
                <span className="tabular-nums" data-testid={`won-gst-amount-${index}`}>{format(gstAmount)}</span>
              </div>
              <div className="flex items-center justify-between border-t border-emerald-200 pt-1.5">
                <span className="font-semibold text-emerald-800">Grand Total</span>
                <span className="text-base font-bold text-emerald-800 tabular-nums" data-testid={`won-grand-total-${index}`}>{format(grandTotal)}</span>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default function WonDetailsDialog({ prompt, onClose, onSaved, onNotTaken }) {
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);
  const { format } = useCurrency();

  useEffect(() => {
    const d = prompt?.deal;
    if (!d) return;
    setRows(dealProducts(d).map(toRow));
  }, [prompt]);

  if (!prompt) return null;
  const deal = prompt.deal;
  const isEdit = !!prompt.edit;
  const many = rows.length > 1;
  const wonRows = rows.filter((r) => r.status === "won");
  const totals = wonRows.reduce((acc, r) => {
    const a = wonAmounts({ quantity_mt: r.qty, exw_price: r.exw, freight_amount: r.freight, gst_percent: r.gst });
    return {
      exw: acc.exw + a.exwAmount, freight: acc.freight + a.f, forAmt: acc.forAmt + a.forAmount,
      gst: acc.gst + a.gstAmount, grand: acc.grand + a.grandTotal, qty: acc.qty + a.q,
    };
  }, { exw: 0, freight: 0, forAmt: 0, gst: 0, grand: 0, qty: 0 });

  const rowComplete = (r) => r.pi.trim().length > 0 && Number(r.qty) > 0 && Number(r.exw) > 0;
  const lostRows = rows.filter((r) => r.status === "not_taken");
  const lostComplete = (r) => r.lostReason.trim().length >= LOST_REASON_MIN;
  const canSubmit = wonRows.length > 0 && wonRows.every(rowComplete) && lostRows.every(lostComplete);

  const onChange = (index, patch) =>
    setRows((rs) => rs.map((r, i) => (i === index ? { ...r, ...patch } : r)));

  const submit = async (e) => {
    e.preventDefault();
    if (wonRows.length === 0) {
      toast.error("Mark at least one product as Won", {
        description: "If the customer took nothing, move this deal to Lost instead.",
      });
      return;
    }
    const missing = wonRows.filter((r) => !rowComplete(r)).map((r) => r.product_name || "product");
    if (missing.length) {
      toast.error(`Fill in PI number, Quantity and EXW price for: ${missing.join(", ")}`);
      return;
    }
    const noReason = lostRows.filter((r) => !lostComplete(r)).map((r) => r.product_name || "product");
    if (noReason.length) {
      toast.error(`Write at least ${LOST_REASON_MIN} characters on why these were lost: ${noReason.join(", ")}`);
      return;
    }
    const products = rows.map((r) => ({
      id: r.id, product_name: r.product_name, product_code: r.product_code, status: r.status,
      lost_reason: r.status === "not_taken" ? r.lostReason.trim() : "",
      po_number: r.status === "won" ? r.po.trim() : "",
      pi_number: r.status === "won" ? r.pi.trim() : "",
      quantity_mt: r.status === "won" ? Number(r.qty) : null,
      exw_price: r.status === "won" ? Number(r.exw) : null,
      freight_amount: r.status === "won" ? Number(r.freight) || 0 : null,
      gst_percent: r.status === "won" ? Number(r.gst) || 0 : null,
    }));
    setSaving(true);
    try {
      if (isEdit) {
        await api.patch(`/deals/${deal.id}/won-details`, { products });
        toast.success(`Won details updated — ${format(totals.exw)} booked`, {
          description: "The correction has been recorded in the change history.",
        });
      } else {
        await api.patch(`/deals/${deal.id}/stage`, { stage: "Won", products });
        celebrate();
        toast.success(`🏆 ${deal.contact_name || "Deal"} won — ${format(totals.exw)} booked!`, {
          description: `${wonRows.length} product${wonRows.length === 1 ? "" : "s"} · ${totals.qty} MT`,
          duration: 5000,
        });
      }
      onSaved();
      onClose();
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
    setSaving(false);
  };

  return (
    <Dialog open={!!prompt} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-emerald-700 flex items-center gap-2">
            <Trophy className="w-5 h-5" />
            {isEdit ? "Edit Won / PI Details" : "Mark Deal as Won"}
          </DialogTitle>
        </DialogHeader>

        <div className="border border-border rounded-md bg-secondary/40 p-3 space-y-1 text-sm">
          <div className="font-semibold">{deal.contact_name}</div>
          <div className="text-muted-foreground text-xs">
            {rows.length} product{rows.length === 1 ? "" : "s"} on this deal
          </div>
          <div className="flex items-center gap-4 text-xs text-muted-foreground pt-1 flex-wrap">
            <span>Owner: <span className="text-neutral-800 font-medium">{deal.created_by_name || "—"}</span></span>
            <span>Estimated: <span className="text-neutral-800 font-medium">{format(deal.estimated_value)}</span></span>
            {isEdit
              ? <span>Currently booked: <span className="text-emerald-700 font-semibold">{deal.actual_value != null ? format(deal.actual_value) : "—"}</span></span>
              : <span>From: <span className="text-neutral-800 font-medium">{deal.stage}</span> → <span className="text-emerald-700 font-semibold">Won</span></span>}
          </div>
        </div>

        <form onSubmit={submit} className="mt-3 space-y-3">
          {many && (
            <p className="text-xs text-muted-foreground">
              Mark what the customer actually took, then fill in the invoice figures for each won product.
            </p>
          )}
          {rows.map((r, i) => (
            <ProductBlock key={r.key} row={r} index={i} many={many} onChange={onChange} format={format} />
          ))}

          {many && (
            <div className="rounded-md border-2 border-emerald-300 bg-emerald-50 p-3 space-y-1.5 text-sm" data-testid="won-order-total">
              <div className="text-[10px] uppercase tracking-widest text-emerald-800 font-semibold pb-1 border-b border-emerald-200">
                Order total · {wonRows.length} of {rows.length} products won{totals.qty > 0 ? ` · ${totals.qty} MT` : ""}
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Total EXW Value (booked)</span>
                <span className="font-semibold tabular-nums" data-testid="order-exw-amount">{format(totals.exw)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Total Freight</span>
                <span className="tabular-nums" data-testid="order-freight-amount">{format(totals.freight)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Total GST</span>
                <span className="tabular-nums" data-testid="order-gst-amount">{format(totals.gst)}</span>
              </div>
              <div className="flex items-center justify-between border-t border-emerald-200 pt-1.5">
                <span className="font-semibold text-emerald-800">Grand Total</span>
                <span className="text-base font-bold text-emerald-800 tabular-nums" data-testid="order-grand-total">{format(totals.grand)}</span>
              </div>
            </div>
          )}

          {wonRows.length === 0 && (
            <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-xs text-destructive space-y-2" data-testid="won-none-selected">
              <div>Every product is marked "Not taken" — this deal belongs in <strong>Lost</strong> instead.</div>
              {!isEdit && onNotTaken && (
                <Button type="button" variant="outline" size="sm" data-testid="won-move-to-lost"
                  onClick={() => onNotTaken(deal)}
                  className="h-7 px-2 text-[11px] border-destructive/40 text-destructive hover:bg-destructive/10">
                  Move to Lost and add the reason
                </Button>
              )}
            </div>
          )}

          <div className="text-[10px] text-muted-foreground">
            Reporting uses the Total EXW Value ({format(totals.exw)}) as this deal's booked value.
          </div>

          <div className="flex justify-end gap-2 border-t border-border pt-3">
            <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
            <Button data-testid="won-submit" type="submit" disabled={saving || !canSubmit}
              className="bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50">
              {saving ? "Saving…" : isEdit ? "Save Corrections" : "Save & Mark as Won"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
